import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, ProductContextModel, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseDto, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import Info from "@insite/mobius/Icons/Info";
import Link from "@insite/mobius/Link";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import FieldSetPresentationProps from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DeliveryTruckLine from "@store/Components/Icons/DeliveryTruckLine";
import ShipToStoreLine from "@store/Components/Icons/ShipToStoreLine";
import StorePickupLine from "@store/Components/Icons/StorePickupLine";
import StoreShipLine from "@store/Components/Icons/StoreShipLine";
import ProductReadyBy from "@store/Components/Store/Product/ProductReadyBy";
import ProductContextAvailability from "@store/Overrides/Components/ProductContextAvailability";
import { StoreFulfillmentMethod } from "@store/Services/SessionService";
import setAddressBookIsOpen from "@store/Store/Components/AddressBookModal/Handlers/SetAddressBookModalIsOpen";
import { getStoreSession, getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import { loadingDotsAnimation, scalableIconStyles } from "@store/theme/common/mixins";
import { fulfillmentMethodRadioWrapperBaseStyles, fulfillmentMethodRadioWrapperSelectedStyles, fulfillmentMethodRadioWrapperUnselectedStyles, fulfillmentMethodShape } from "@store/theme/common/mixins/fulfillmentMethod";
import { TYPOGRAPHY } from "@store/theme/design";
import getFulfillmentAvailabilities from "@store/utilities/getFulfillmentAvailabilities";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductContextModel;
    shipQty: number | undefined;
    pickupQty: number | undefined;
    isHighDemandMode: boolean;
    nearbyWarehouse?: WarehouseDto;
    changeWarehouse: (requestedFulfillmentMethod: string) => void;
    selectedFulfillmentMethod?: string;
    changeFulfillmentMethod: (requestedFulfillmentMethod: string) => void;
}

type Props = OwnProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => {
    const poolcorpSettings = getStoreSettingsCollection(state).poolcorpSettings;
    const session = getStoreSession(state);
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;

    return {
        shipToState: getShipToState(state, cart?.shipToId),
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        pageType: getCurrentPage(state).type,
        pickupNearbyDisplay: poolcorpSettings.pickupNearbyDisplay,
        fulfillmentMethod: session.fulfillmentMethod,
        showGlobalQuantityInventory: poolcorpSettings.showGlobalQuantityInventory,
    }
};

const mapDispatchToProps = {
    setAddressBookIsOpen,
};

export interface LineFulfilmentStyles {
    fulfillmentMethodRadioWrapper?: InjectableCss;
    fulfillmentMethodRadioWrapperSelected?: InjectableCss;
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    radioGroupWrapper?: InjectableCss;
    descriptonTitle?: InjectableCss;
    serviceDescription: TypographyPresentationProps;
    postalCode: TypographyPresentationProps;
    address: InjectableCss;
    inStock: TypographyPresentationProps;
    outOfStock: TypographyPresentationProps;
    fulfillmentDelay: TypographyPresentationProps;
    deliveryIcon?: IconPresentationProps;
    selectedDeliveryIcon?: IconPresentationProps;
    shippingIcon?: IconPresentationProps;
    selectedShippingIcon?: IconPresentationProps;
    pickupIcon?: IconPresentationProps;
    selectedPickupIcon?: IconPresentationProps;
    shipToStoreIcon?: IconPresentationProps;
    selectedShipToStoreIcon?: IconPresentationProps;
    fulfillmentMethodRadio?: FieldSetPresentationProps<RadioComponentProps>;
    placeholderText?: TypographyPresentationProps;
    infoIconShipToStore?: IconProps;
    infoIconPickUp?: IconProps;
}

const fulfillmentMethodRadioWrapperStyles = css`
    ${fulfillmentMethodRadioWrapperBaseStyles(fulfillmentMethodShape.rectangle)}
    display: flex;
    width: 100%;
    min-height: 66px;
    align-items: center;
    transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;

    &:hover {
        border-color: ${getColor("primary")};
    };

    &:hover .icon-class {
        color: ${getColor("primary")};
    }
`;


const iconStyles = {
    size: 26,
};

const infoIconStyles = {
    src: Info,
    css: css`
        ${scalableIconStyles()}
        top: 0.175em;
        margin-left: 0.25em;
        font-size: 1.1em;
    `,
}

const iconUnselectedStyles = {
    ...iconStyles,
    css: css`
        transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;

        &:hover {
            color: ${getColor("common.disabledDark")};
        }
    `,
};

const iconSelectedStyles = {
    ...iconStyles,
    css: css`
        transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;

        &:hover {
            color: ${getColor("primary.light")};
        }
    `,
};

export const lineFulfilmentStyles: LineFulfilmentStyles = {
    fulfillmentMethodRadioWrapper: {
        css: css`
            ${fulfillmentMethodRadioWrapperStyles}
            ${fulfillmentMethodRadioWrapperUnselectedStyles}
        `,
    },
    fulfillmentMethodRadioWrapperSelected: {
        css: css`
            ${fulfillmentMethodRadioWrapperStyles}
            ${fulfillmentMethodRadioWrapperSelectedStyles}
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
            margin-left: .5em;
            font-size: ${TYPOGRAPHY.size.smaller};
            overflow: hidden;
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
        `,
    },
    radioGroupWrapper: {
        css: css`
            min-width: 0px;
        `,
    },
    descriptonTitle: {
        css: css`
            flex-shrink: 0;
            margin-right: 0.25em;
        `,
    },
    serviceDescription: {
        weight: TYPOGRAPHY.weight.bold,
    },
    postalCode: {
        css: css`
            color: ${getColor("primary")};
        `,
    },
    address: {
        css: css`
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    inStock: {
        color: "success",
    },
    outOfStock: {
        color: "danger",
    },
    fulfillmentDelay: {
        weight: TYPOGRAPHY.weight.bold,
        color: "success",
    },
    deliveryIcon: {
        ...iconUnselectedStyles,
        color: "common.disabledDark",
        src: DeliveryTruckLine,
    },
    selectedDeliveryIcon: {
        ...iconSelectedStyles,
        color: "primary",
        src: DeliveryTruckLine,
    },
    shippingIcon: {
        ...iconUnselectedStyles,
        color: "common.disabledDark",
        src: StoreShipLine,
    },
    selectedShippingIcon: {
        ...iconSelectedStyles,
        color: "primary",
        src: StoreShipLine,
    },
    pickupIcon: {
        ...iconUnselectedStyles,
        color: "common.disabledDark",
        src: StorePickupLine,
    },
    selectedPickupIcon: {
        ...iconSelectedStyles,
        color: "primary",
        src: StorePickupLine,
    },
    shipToStoreIcon: {
        ...iconUnselectedStyles,
        color: "common.disabledDark",
        src: ShipToStoreLine,
    },
    selectedShipToStoreIcon: {
        ...iconSelectedStyles,
        color: "primary",
        src: ShipToStoreLine,
    },
    fulfillmentMethodRadio: {
        css: css`
            input {
                position: absolute;
            }

            input + label {
                height: 100%;
                width: 100%;
                margin: 0;
                cursor: pointer;

                &::before, &::after {
                    display: none !important;
                }
            }

        `,
    },
    placeholderText: {
        css: css`
            ${loadingDotsAnimation()}
        `,
    },
    infoIconShipToStore: {
        ...infoIconStyles,
        color: "success",
    },
    infoIconPickUp: {
        ...infoIconStyles,
        color: "primary",
    },
};
const styles = lineFulfilmentStyles;

const ProductFulfillment: React.FC<Props> = ({
    product,
    shipQty,
    pickupQty,
    isHighDemandMode,
    shipToState,
    pickUpWarehouse,
    pageType,
    pickupNearbyDisplay,
    fulfillmentMethod,
    nearbyWarehouse,
    changeWarehouse,
    selectedFulfillmentMethod,
    changeFulfillmentMethod,
    showGlobalQuantityInventory,
    setAddressBookIsOpen,
}) => {
    const productContextModel = product as ProductContextModel;
    const productShipVias = productContextModel.product.properties.productShipVias;
    const {isPickupAvailable, isShipAvailable, isDeliveryAvailable} = getFulfillmentAvailabilities(productShipVias);

    const isPagePDP = pageType === "ProductDetailsPage";
    const [displayPlaceholder, setDisplayPlaceholder] = useState(true);
    const [displayShipToStore, setDisplayShipToStore] = useState(false);
    const [displayPickup, setDisplayPickup] = useState(false);
    const [displayPickupNearby, setDisplayPickupNearby] = useState(false);
    const [pickupQtyMessage, setPickupQtyMessage] = useState(<></>);
    const [shipQtyMessage, setShipQtyMessage] = useState(<></>);

    const onKeyPressed = (event: React.KeyboardEvent, fulfillmentMethod: string) => {
        if (event.keyCode === 13) {
            changeFulfillmentMethod(fulfillmentMethod);
        }
    }

    const openAddressBook = () => {
        setAddressBookIsOpen(true);
    }

    useEffect(() => {
        if (pickupQty !== undefined) {
            setDisplayPlaceholder(false);
            setDisplayPickup(pickupQty > 0);
            setPickupQtyMessage(stockMessage(pickupQty));
        }

        if (pickupQty === 0) {
            // Only display pickup nearby on PDP. If there is no stock in customer radius, the out of stock display can be disabled with pickupNearbyDisplay config.
            const displayPickupNearby = isPagePDP && (Boolean(nearbyWarehouse) || pickupNearbyDisplay);

            setDisplayShipToStore(true);
            setDisplayPickupNearby(displayPickupNearby);

            // Change fulfillment method to "Ship To Store" if quantity is 0 and if global selected method is PickUp
            if (fulfillmentMethod === StoreFulfillmentMethod.PickUp) {
                changeFulfillmentMethod(StoreFulfillmentMethod.ShipToStore);
            }
        }
    }, [pickupQty]);

    useEffect(() => {
        if (shipQty !== undefined) {
            setShipQtyMessage(stockMessage(shipQty));
        }

    }, [shipQty]);

    const handleChangeFulfillment = (event: ChangeEvent<HTMLInputElement>) => {
        const fulfillmentMethod = event.currentTarget.value;

        changeFulfillmentMethod(fulfillmentMethod);

        if (fulfillmentMethod === StoreFulfillmentMethod.PickUp && nearbyWarehouse) {
            changeWarehouse(nearbyWarehouse.name);
        } else if (pickUpWarehouse) {
            changeWarehouse(pickUpWarehouse.name);
        }
    };

    const stockMessage = (quantity: number) => {
        let message = <Typography></Typography>;

        if (quantity > 0 && !isHighDemandMode) {
            message = <Typography {...styles.inStock}>({translate("{0} in stock").replace("{0}", quantity.toString())})&nbsp;</Typography>
        } else if (quantity > 0 && isHighDemandMode) {
            message = <Typography {...styles.inStock}>({translate("In stock")})&nbsp;</Typography>
        } else {
            message = <Typography {...styles.outOfStock}>({translate("Out of stock")})&nbsp;</Typography>
        }

        return message;
    };

    const clickablePickUpWarehouse = (warehouse: WarehouseDto | WarehouseModel) => {
        return (
            <StyledWrapper {...styles.address}>
                <ProductContextAvailability
                    clickableComponent={
                        <>
                            <Typography>{`${warehouse.name} ${warehouse.description}`}</Typography>
                            <Icon {...styles.infoIconPickUp} />
                        </>
                    }
                />
            </StyledWrapper>
        )
    };

    const clickableShipTo = () => {
        return (
            shipToState && (
                <StyledWrapper {...styles.address}>
                    <Link onClick={openAddressBook}>
                        <Typography as="span">{shipToState.value?.fullAddress}</Typography>
                    </Link>
                </StyledWrapper>
            )
        )
    };

    const shipToStoreMessage = () => {
        if (isPagePDP) {
            return (
                pickUpWarehouse && (
                    <ProductReadyBy
                        warehouse={pickUpWarehouse.name}
                        fulfillmentMethod={StoreFulfillmentMethod.PickUp}
                    />
                )
            )
        }

        return (
            nearbyWarehouse && (
                <ProductContextAvailability
                    clickableComponent={
                        <>
                            <Typography {...styles.inStock}>{`${translate("Available Nearby")}`}</Typography>
                            <Icon {...styles.infoIconShipToStore} />
                        </>
                    }
                />
            )
        )
    };

    type FulfillmentTileProps = {
        storeFulfillmentMethod: string,
        isDisabled: boolean,
        selectedIconStyles: IconPresentationProps | undefined,
        iconStyles: IconPresentationProps| undefined,
    }

    const FulfillmentTile: React.FC<FulfillmentTileProps> = ({
        storeFulfillmentMethod,
        isDisabled,
        selectedIconStyles,
        iconStyles,
        children,
    }) => {
        return (
            <Radio
                value={storeFulfillmentMethod}
                disabled={isDisabled}
                tabIndex={-1}
                {...styles.fulfillmentMethodRadio}
            >
                <StyledWrapper
                    tabIndex={0}
                    onKeyDown={(event) => onKeyPressed(event, storeFulfillmentMethod)}
                    {...selectedFulfillmentMethod === storeFulfillmentMethod
                        ? styles.fulfillmentMethodRadioWrapperSelected
                        : styles.fulfillmentMethodRadioWrapper
                    }
                >
                    <Icon
                        className="icon-class"
                        {...selectedFulfillmentMethod === storeFulfillmentMethod
                            ? selectedIconStyles
                            : iconStyles
                        }
                    />
                    <StyledWrapper {...styles.wrapper}>
                        {children}
                    </StyledWrapper>
                </StyledWrapper>
            </Radio>
        );
    }

    return (
        <>
            <RadioGroup
                value={selectedFulfillmentMethod}
                onChangeHandler={handleChangeFulfillment}
                {...styles.radioGroupWrapper}
            >
                {displayPlaceholder && (
                    <FulfillmentTile
                        storeFulfillmentMethod={StoreFulfillmentMethod.PickUp}
                        isDisabled={!isPickupAvailable}
                        selectedIconStyles={styles.selectedPickupIcon}
                        iconStyles={styles.pickupIcon}
                    >
                        <StyledWrapper {...styles.innerWrapper}>
                            <StyledWrapper {...styles.descriptonTitle}>
                                <Typography {...styles.serviceDescription}>
                                    {translate("Store Pickup at")}:&nbsp;
                                    <Typography {...styles.placeholderText}></Typography>
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper>
                            <Typography {...styles.placeholderText}></Typography>
                        </StyledWrapper>
                    </FulfillmentTile>
                )}
                {displayShipToStore && (
                    <FulfillmentTile
                        storeFulfillmentMethod={StoreFulfillmentMethod.ShipToStore}
                        isDisabled={!isPickupAvailable}
                        selectedIconStyles={styles.selectedShipToStoreIcon}
                        iconStyles={styles.shipToStoreIcon}
                    >
                        <StyledWrapper {...styles.innerWrapper}>
                            <StyledWrapper {...styles.descriptonTitle}>
                                <Typography {...styles.serviceDescription}>
                                    {translate("Ship to Store")}:&nbsp;
                                </Typography>
                            </StyledWrapper>
                            {pickUpWarehouse && clickablePickUpWarehouse(pickUpWarehouse)}
                        </StyledWrapper>
                        <StyledWrapper>
                            {shipToStoreMessage()}
                        </StyledWrapper>
                    </FulfillmentTile>
                )}
                {displayPickup && (
                    <FulfillmentTile
                        storeFulfillmentMethod={StoreFulfillmentMethod.PickUp}
                        isDisabled={!isPickupAvailable}
                        selectedIconStyles={styles.selectedPickupIcon}
                        iconStyles={styles.pickupIcon}
                    >
                        <StyledWrapper {...styles.innerWrapper}>
                            <StyledWrapper {...styles.descriptonTitle}>
                                <Typography {...styles.serviceDescription}>
                                    {(translate("Store pickup at"))}:&nbsp;
                                </Typography>
                            </StyledWrapper>
                            {pickUpWarehouse && clickablePickUpWarehouse(pickUpWarehouse)}
                        </StyledWrapper>
                        <StyledWrapper>
                            {pickupQtyMessage}
                            {pickUpWarehouse && (
                                <ProductReadyBy
                                    warehouse={pickUpWarehouse.name}
                                    fulfillmentMethod={StoreFulfillmentMethod.PickUp}
                                />
                            )}
                        </StyledWrapper>
                    </FulfillmentTile>
                )}
                {displayPickupNearby && (
                    <FulfillmentTile
                        storeFulfillmentMethod={StoreFulfillmentMethod.PickUp}
                        isDisabled={nearbyWarehouse ? !isPickupAvailable : true}
                        selectedIconStyles={styles.selectedPickupIcon}
                        iconStyles={styles.pickupIcon}
                    >
                        <StyledWrapper {...styles.innerWrapper}>
                            <StyledWrapper {...styles.descriptonTitle}>
                                <Typography {...styles.serviceDescription}>
                                    {(translate("Pickup Nearby"))}:&nbsp;
                                </Typography>
                            </StyledWrapper>
                            {nearbyWarehouse && clickablePickUpWarehouse(nearbyWarehouse)}
                        </StyledWrapper>
                        <StyledWrapper>
                            {nearbyWarehouse ? (
                                <>
                                    {stockMessage(nearbyWarehouse.qty)}
                                    <ProductReadyBy
                                        warehouse={nearbyWarehouse.name}
                                        fulfillmentMethod={StoreFulfillmentMethod.PickUp}
                                    />
                                </>
                            ) : (
                                <Typography  {...styles.outOfStock}>{translate("Not available for pickup in selected radius.")}</Typography>
                            )}
                        </StyledWrapper>
                    </FulfillmentTile>
                )}
                <FulfillmentTile
                    storeFulfillmentMethod={StoreFulfillmentMethod.Delivery}
                    isDisabled={!isDeliveryAvailable}
                    selectedIconStyles={styles.selectedDeliveryIcon}
                    iconStyles={styles.deliveryIcon}
                >
                    <StyledWrapper {...styles.innerWrapper}>
                        <StyledWrapper {...styles.descriptonTitle}>
                            <Typography {...styles.serviceDescription}>
                                {translate("POOL Delivery to")}:&nbsp;
                            </Typography>
                        </StyledWrapper>
                        {clickableShipTo()}
                    </StyledWrapper>
                    <StyledWrapper>
                        {pickUpWarehouse && (
                            <ProductReadyBy
                                warehouse={pickUpWarehouse.name}
                                fulfillmentMethod={StoreFulfillmentMethod.Delivery}
                            />
                        )}
                    </StyledWrapper>
                </FulfillmentTile>
                <FulfillmentTile
                    storeFulfillmentMethod={StoreFulfillmentMethod.Ship}
                    isDisabled={!isShipAvailable}
                    selectedIconStyles={styles.selectedShippingIcon}
                    iconStyles={styles.shippingIcon}
                >
                    <StyledWrapper {...styles.innerWrapper}>
                        <StyledWrapper {...styles.descriptonTitle}>
                            <Typography {...styles.serviceDescription}>
                                {translate("Ship it to")}:&nbsp;
                            </Typography>
                        </StyledWrapper>
                        {clickableShipTo()}
                    </StyledWrapper>
                    <StyledWrapper>
                        {showGlobalQuantityInventory && shipQtyMessage}
                        {pickUpWarehouse && (
                            <ProductReadyBy
                                warehouse={pickUpWarehouse.name}
                                fulfillmentMethod={StoreFulfillmentMethod.Ship}
                            />
                        )}
                    </StyledWrapper>
                </FulfillmentTile>
            </RadioGroup>
        </>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductFulfillment));
